import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { api, fmtEUR, tradeLabel, TRADE_OPTIONS } from "@/lib/api";
import EditDevisHeaderDialog from "@/components/EditDevisHeaderDialog";
import ReliabilityBadge from "@/components/ReliabilityBadge";
import {
    CheckCircle2,
    AlertTriangle,
    XCircle,
    Plus,
    Trash2,
    Edit3,
    Save,
    Wand2,
    ChevronDown,
    ChevronRight,
    Info,
    Sparkles,
    Loader2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
    ok: {
        label: "Dans le budget",
        cls: "border-ok text-ok bg-ok/5",
        icon: CheckCircle2,
    },
    over_soft: {
        label: "Léger dépassement",
        cls: "border-warn text-warn bg-warn/10",
        icon: AlertTriangle,
    },
    over_hard: {
        label: "Budget dépassé",
        cls: "border-danger text-danger bg-danger/10",
        icon: XCircle,
    },
    no_budget: {
        label: "Sans cible",
        cls: "border-ink/30 text-ink-soft bg-[#FAFAFA]",
        icon: null,
    },
    no_devis: {
        label: "Aucun devis",
        cls: "border-ink/30 text-ink-soft bg-[#FAFAFA]",
        icon: null,
    },
};

export default function BudgetTable({ projectId, refreshKey, onChange }) {
    const [summary, setSummary] = useState(null);
    const [devisById, setDevisById] = useState({});
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({}); // {trade: amountString}
    const [newRow, setNewRow] = useState({ trade: "", amount: "" });
    const [saving, setSaving] = useState(false);
    const [expanded, setExpanded] = useState({}); // {trade: boolean}
    const [toggling, setToggling] = useState(null); // devis_id being toggled

    const load = async () => {
        try {
            const [s, devisList] = await Promise.all([
                api.getBudgetSummary(projectId),
                api.listDevis(projectId),
            ]);
            setSummary(s);
            setDevisById(Object.fromEntries(devisList.map((d) => [d.id, d])));
            const d = {};
            s.rows.forEach((r) => {
                if (r.budget) d[r.trade] = String(r.budget);
            });
            setDraft(d);
        } catch (e) {
            console.error("BudgetTable load failed:", e);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [projectId, refreshKey]);

    const toggleInclude = async (devisId, currentStatus) => {
        const newStatus = currentStatus === "valide" ? "recu" : "valide";
        setToggling(devisId);
        try {
            await api.updateDevis(devisId, { status: newStatus });
            await load();
            onChange && onChange();
        } catch {
            toast.error("Erreur lors de la mise à jour");
        } finally {
            setToggling(null);
        }
    };

    const toggleExpand = (trade) =>
        setExpanded((prev) => ({ ...prev, [trade]: !prev[trade] }));

    const [autoDetecting, setAutoDetecting] = useState(null); // trade being analyzed

    const setBestOverride = async (r, amount) => {
        try {
            await api.setBestOverride(projectId, r.trade, amount || 0);
            toast.success(
                amount && amount > 0
                    ? "Meilleure offre saisie manuellement"
                    : "Saisie manuelle annulée — calcul automatique réactivé"
            );
            await load();
            onChange && onChange();
        } catch {
            toast.error("Erreur d'enregistrement");
        }
    };

    const autoDetectComplementary = async (r) => {
        setAutoDetecting(r.trade);
        try {
            const res = await api.suggestComplementary(projectId, r.trade);
            if (res.mode === "competing" || (res.complementary_devis_ids || []).length === 0) {
                toast.info(
                    "IA : ces devis sont concurrents — un seul à retenir. " +
                        (res.reasoning || "")
                );
                return;
            }
            const targetIds = new Set(res.complementary_devis_ids);
            // Validate target ids, unvalidate the rest (only for this trade)
            const ops = [];
            (r.devis || []).forEach((dv) => {
                const shouldBe = targetIds.has(dv.id);
                const isValid = dv.status === "valide";
                if (shouldBe !== isValid) {
                    ops.push(
                        api.updateDevis(dv.id, {
                            status: shouldBe ? "valide" : "recu",
                        })
                    );
                }
            });
            await Promise.all(ops);
            toast.success(
                `IA : ${targetIds.size} devis complémentaires détectés et cochés. ${
                    res.reasoning || ""
                }`
            );
            await load();
            onChange && onChange();
        } catch {
            toast.error("Erreur lors de l'analyse IA");
        } finally {
            setAutoDetecting(null);
        }
    };

    if (!summary) return null;

    const hasAny = summary.rows.length > 0;

    const startEdit = () => {
        const d = {};
        summary.rows.forEach((r) => {
            d[r.trade] = r.budget ? String(r.budget) : "";
        });
        setDraft(d);
        setNewRow({ trade: "", amount: "" });
        setEditing(true);
    };

    const cancelEdit = () => {
        setEditing(false);
        setNewRow({ trade: "", amount: "" });
    };

    const clearBudget = (trade) => {
        setDraft((prev) => ({ ...prev, [trade]: "" }));
    };

    const saveBudgets = async () => {
        setSaving(true);
        try {
            const payload = {};
            Object.entries(draft).forEach(([k, v]) => {
                const n = parseFloat(String(v).replace(",", "."));
                if (!isNaN(n) && n > 0) payload[k.trim().toLowerCase()] = n;
            });
            if (newRow.trade.trim() && newRow.amount) {
                const n = parseFloat(String(newRow.amount).replace(",", "."));
                if (!isNaN(n) && n > 0) {
                    payload[newRow.trade.trim().toLowerCase()] = n;
                }
            }
            await api.setBudgets(projectId, payload);
            toast.success("Budget enregistré");
            setEditing(false);
            setNewRow({ trade: "", amount: "" });
            await load();
            onChange && onChange();
        } catch (e) {
            toast.error("Erreur d'enregistrement");
        } finally {
            setSaving(false);
        }
    };

    const tot = summary.totals;
    const totMeta = STATUS_META[tot.status] || STATUS_META.no_budget;

    return (
        <section className="panel" data-testid="budget-section">
            <div className="panel-header">
                <div>
                    <div className="stat-label">Marché de travaux</div>
                    <h2 className="font-heading text-xl font-bold mt-0.5">
                        Budget par poste
                    </h2>
                </div>
                {!editing ? (
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            className="rounded-none border-ink"
                            onClick={async () => {
                                try {
                                    const r = await api.normalizeTrades(
                                        projectId
                                    );
                                    toast.success(
                                        `${r.devis_normalized} devis réorganisés${
                                            r.budgets_normalized
                                                ? " · budgets fusionnés"
                                                : ""
                                        }`
                                    );
                                    await load();
                                    onChange && onChange();
                                } catch {
                                    toast.error("Erreur");
                                }
                            }}
                            data-testid="normalize-trades-btn"
                            title="Fusionne les variantes de corps de métier (placo/plaquiste/plâtrier…) sur une seule ligne"
                        >
                            <Wand2
                                className="w-4 h-4 mr-2"
                                strokeWidth={1.6}
                            />
                            Réorganiser les postes
                        </Button>
                        <Button
                            variant="outline"
                            className="rounded-none border-ink"
                            onClick={startEdit}
                            data-testid="budget-edit-btn"
                        >
                            <Edit3
                                className="w-4 h-4 mr-2"
                                strokeWidth={1.6}
                            />
                            {hasAny ? "Modifier" : "Définir les budgets"}
                        </Button>
                    </div>
                ) : (
                    <div className="flex gap-2">
                        <Button
                            variant="outline"
                            className="rounded-none"
                            onClick={cancelEdit}
                            disabled={saving}
                        >
                            Annuler
                        </Button>
                        <Button
                            className="rounded-none bg-ink text-white hover:bg-ink/90"
                            onClick={saveBudgets}
                            disabled={saving}
                            data-testid="budget-save-btn"
                        >
                            <Save className="w-4 h-4 mr-2" strokeWidth={1.6} />
                            {saving ? "Enregistrement…" : "Enregistrer"}
                        </Button>
                    </div>
                )}
            </div>

            {!hasAny && !editing ? (
                <div className="p-10 text-center">
                    <div className="font-heading text-lg font-bold mb-1">
                        Aucun budget défini
                    </div>
                    <p className="text-sm text-ink-soft mb-4 max-w-md mx-auto">
                        Fixez un objectif HT par corps de métier. L'app
                        comparera la meilleure offre reçue à votre cible et
                        passera en rouge dès que le budget est dépassé.
                    </p>
                    <Button
                        className="rounded-none bg-ink text-white hover:bg-ink/90"
                        onClick={startEdit}
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        Définir un budget
                    </Button>
                </div>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm border-collapse">
                            <thead>
                                <tr className="bg-[#FAFAFA] border-b border-ink">
                                    <th className="text-left px-5 py-3 stat-label">
                                        Poste
                                    </th>
                                    <th className="text-right px-5 py-3 stat-label">
                                        Budget HT
                                    </th>
                                    <th className="text-right px-5 py-3 stat-label">
                                        Meilleure offre
                                    </th>
                                    <th className="text-right px-5 py-3 stat-label">
                                        Écart
                                    </th>
                                    <th className="text-left px-5 py-3 stat-label">
                                        Statut
                                    </th>
                                    {editing && (
                                        <th className="w-12"></th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {summary.rows.map((r) => {
                                    const meta =
                                        STATUS_META[r.status] ||
                                        STATUS_META.no_budget;
                                    const Icon = meta.icon;
                                    const rowHi =
                                        r.status === "over_hard"
                                            ? "bg-danger/5"
                                            : r.status === "over_soft"
                                            ? "bg-warn/5"
                                            : "";
                                    const isOpen = !!expanded[r.trade];
                                    const hasDevis = (r.devis || []).length > 0;
                                    return (
                                        <FragmentRow
                                            key={r.trade}
                                            r={r}
                                            meta={meta}
                                            Icon={Icon}
                                            rowHi={rowHi}
                                            isOpen={isOpen}
                                            hasDevis={hasDevis}
                                            editing={editing}
                                            draft={draft}
                                            setDraft={setDraft}
                                            clearBudget={clearBudget}
                                            toggleExpand={toggleExpand}
                                            toggleInclude={toggleInclude}
                                            toggling={toggling}
                                            onAutoDetect={() => autoDetectComplementary(r)}
                                            autoDetecting={autoDetecting === r.trade}
                                            onSetOverride={setBestOverride}
                                        />
                                    );
                                })}
                                {editing && (
                                    <tr className="border-b border-border bg-[#FAFAFA]">
                                        <td className="px-5 py-3">
                                            <Select
                                                value={newRow.trade}
                                                onValueChange={(v) =>
                                                    setNewRow({
                                                        ...newRow,
                                                        trade: v,
                                                    })
                                                }
                                            >
                                                <SelectTrigger
                                                    data-testid="budget-new-trade"
                                                    className="rounded-none h-9"
                                                >
                                                    <SelectValue placeholder="Ajouter un poste…" />
                                                </SelectTrigger>
                                                <SelectContent className="rounded-none max-h-[260px]">
                                                    {TRADE_OPTIONS.filter(
                                                        (opt) =>
                                                            !summary.rows.find(
                                                                (r) =>
                                                                    r.trade ===
                                                                    opt.value
                                                            )
                                                    ).map((opt) => (
                                                        <SelectItem
                                                            key={opt.value}
                                                            value={opt.value}
                                                        >
                                                            {opt.label}
                                                        </SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </td>
                                        <td className="px-5 py-3 text-right">
                                            <Input
                                                type="number"
                                                placeholder="Budget HT €"
                                                className="rounded-none h-9 num text-right w-32 ml-auto"
                                                value={newRow.amount}
                                                onChange={(e) =>
                                                    setNewRow({
                                                        ...newRow,
                                                        amount: e.target.value,
                                                    })
                                                }
                                                data-testid="budget-new-amount"
                                            />
                                        </td>
                                        <td colSpan={4} className="px-5 py-3 text-xs text-ink-soft">
                                            <span className="inline-flex items-center gap-1.5">
                                                <Plus
                                                    className="w-3 h-3"
                                                    strokeWidth={2}
                                                />
                                                Sélectionnez un corps de
                                                métier, saisissez le budget
                                                puis enregistrez.
                                            </span>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            {summary.rows.length > 0 && (
                                <tfoot>
                                    <tr className="bg-ink text-white">
                                        <td className="px-5 py-3 font-heading font-bold">
                                            Total chantier
                                        </td>
                                        <td className="px-5 py-3 text-right num font-bold">
                                            {fmtEUR(tot.budget)}
                                        </td>
                                        <td className="px-5 py-3 text-right num font-bold">
                                            {fmtEUR(tot.best)}
                                        </td>
                                        <td className="px-5 py-3 text-right num font-bold">
                                            {tot.budget > 0 ? (
                                                <span
                                                    className={
                                                        tot.delta > 0
                                                            ? "text-danger"
                                                            : "text-ok"
                                                    }
                                                >
                                                    {tot.delta > 0 ? "+" : ""}
                                                    {fmtEUR(tot.delta)}
                                                </span>
                                            ) : (
                                                "—"
                                            )}
                                        </td>
                                        <td className="px-5 py-3" colSpan={editing ? 2 : 1}>
                                            <span
                                                className={`pill border ${
                                                    tot.status === "ok"
                                                        ? "border-ok text-ok bg-ok/10"
                                                        : tot.status ===
                                                          "over_soft"
                                                        ? "border-warn text-warn bg-warn/10"
                                                        : tot.status ===
                                                          "over_hard"
                                                        ? "border-danger text-danger bg-danger/10"
                                                        : "border-white/30 text-white bg-white/5"
                                                }`}
                                            >
                                                {totMeta.label}
                                            </span>
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </>
            )}
        </section>
    );
}

function FragmentRow({
    r,
    meta,
    Icon,
    rowHi,
    isOpen,
    hasDevis,
    editing,
    draft,
    setDraft,
    clearBudget,
    toggleExpand,
    toggleInclude,
    toggling,
    onAutoDetect,
    autoDetecting,
    onSetOverride,
}) {
    return (
        <>
            <tr
                className={`border-b border-border ${rowHi}`}
                data-testid={`budget-row-${r.trade}`}
            >
                <td className="px-5 py-3">
                    <div className="flex items-start gap-2">
                        {hasDevis && !editing && (
                            <button
                                onClick={() => toggleExpand(r.trade)}
                                className="mt-0.5 text-ink-soft hover:text-ink"
                                data-testid={`budget-expand-${r.trade}`}
                                title={
                                    isOpen
                                        ? "Replier"
                                        : `Voir les ${r.count} devis et choisir ceux à compter`
                                }
                            >
                                {isOpen ? (
                                    <ChevronDown className="w-4 h-4" strokeWidth={2} />
                                ) : (
                                    <ChevronRight className="w-4 h-4" strokeWidth={2} />
                                )}
                            </button>
                        )}
                        <div>
                            <div className="font-heading font-bold text-sm">
                                {tradeLabel(r.trade)}
                            </div>
                            <div className="text-[11px] text-ink-soft mt-0.5 flex items-center gap-1.5 flex-wrap">
                                <span>
                                    {r.count} devis
                                    {r.best_mode === "sum_validated" && r.validated_count > 0 && (
                                        <span className="text-accent font-semibold">
                                            {" "}· {r.validated_count} retenu(s) — somme
                                        </span>
                                    )}
                                    {r.best_mode === "min_competing" && r.best_contractor && (
                                        <span>
                                            {" "}· min. {r.best_contractor}
                                        </span>
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </td>
                <td className="px-5 py-3 text-right">
                    {editing ? (
                        <Input
                            type="number"
                            className="rounded-none h-9 num text-right w-32 ml-auto"
                            value={draft[r.trade] || ""}
                            onChange={(e) =>
                                setDraft({ ...draft, [r.trade]: e.target.value })
                            }
                            placeholder="0"
                            data-testid={`budget-input-${r.trade}`}
                        />
                    ) : r.budget ? (
                        <span className="num text-sm font-semibold">{fmtEUR(r.budget)}</span>
                    ) : (
                        <span className="text-ink-soft text-xs">—</span>
                    )}
                </td>
                <td className="px-5 py-3 text-right">
                    {r.best_mode === "manual_override" ? (
                        <div className="flex items-center justify-end gap-1.5">
                            <BestEditor r={r} onSave={onSetOverride} onClear={() => onSetOverride(r, 0)} />
                            <span
                                className="pill border-warn text-warn bg-warn/10 text-[10px] py-0"
                                title="Valeur saisie manuellement (remplace la sélection auto)"
                            >
                                manuel
                            </span>
                        </div>
                    ) : r.best ? (
                        <div>
                            <BestEditor r={r} onSave={onSetOverride} onClear={() => onSetOverride(r, 0)} />
                            <div className="text-[10px] text-ink-soft uppercase tracking-wide">
                                {r.best_mode === "sum_validated"
                                    ? `somme de ${r.validated_count}`
                                    : `min. de ${r.count}`}
                            </div>
                        </div>
                    ) : (
                        <BestEditor r={r} onSave={onSetOverride} onClear={() => onSetOverride(r, 0)} placeholder />
                    )}
                </td>
                <td className="px-5 py-3 text-right">
                    {r.delta !== null && r.delta !== undefined ? (
                        <div>
                            <div
                                className={`num text-sm font-bold ${
                                    r.delta > 0 ? "text-danger" : "text-ok"
                                }`}
                            >
                                {r.delta > 0 ? "+" : ""}
                                {fmtEUR(r.delta)}
                            </div>
                            {r.delta_pct !== null && (
                                <div className="text-[10px] text-ink-soft num">
                                    {r.delta_pct > 0 ? "+" : ""}
                                    {r.delta_pct} %
                                </div>
                            )}
                        </div>
                    ) : (
                        <span className="text-ink-soft text-xs">—</span>
                    )}
                </td>
                <td className="px-5 py-3">
                    <span className={`pill ${meta.cls}`}>
                        {Icon && <Icon className="w-3 h-3" strokeWidth={2} />}
                        {meta.label}
                    </span>
                </td>
                {editing && (
                    <td className="px-2 py-3">
                        {(draft[r.trade] || r.budget) && (
                            <button
                                onClick={() => clearBudget(r.trade)}
                                className="text-ink-soft hover:text-danger p-1"
                                title="Retirer le budget"
                            >
                                <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        )}
                    </td>
                )}
            </tr>
            {isOpen && hasDevis && !editing && (
                <tr
                    className="border-b border-border bg-[#FAFAFA]"
                    data-testid={`budget-expanded-${r.trade}`}
                >
                    <td colSpan={5} className="px-5 py-3">
                        <div className="flex items-start gap-2 text-[12px] text-ink-soft mb-3 bg-white border border-border p-2.5">
                            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" strokeWidth={2} />
                            <div>
                                <b>Comment compter ces devis ?</b> Cochez les
                                devis à inclure dans le total budgétaire :
                                <ul className="mt-1 list-disc list-inside space-y-0.5 text-[11px]">
                                    <li>
                                        Devis <b>concurrents</b> (même
                                        périmètre, plusieurs artisans) → cochez
                                        celui que vous retenez (1 seul).
                                    </li>
                                    <li>
                                        Devis <b>complémentaires</b> (périmètres
                                        différents : citerne + plomberie
                                        intérieure + réseaux ext.) → cochez-en
                                        plusieurs, ils seront <b>additionnés</b>.
                                    </li>
                                    <li>
                                        Aucun coché → le système prend
                                        automatiquement le <b>plus bas</b>{" "}
                                        (mode comparaison).
                                    </li>
                                </ul>
                            </div>
                        </div>
                        {r.count >= 2 && (
                            <div className="mb-3">
                                <Button
                                    onClick={onAutoDetect}
                                    disabled={autoDetecting}
                                    variant="outline"
                                    className="rounded-none border-accent text-accent hover:bg-accent hover:text-white w-full"
                                    data-testid={`budget-auto-detect-${r.trade}`}
                                >
                                    {autoDetecting ? (
                                        <Loader2
                                            className="w-4 h-4 mr-2 animate-spin"
                                            strokeWidth={1.6}
                                        />
                                    ) : (
                                        <Sparkles
                                            className="w-4 h-4 mr-2"
                                            strokeWidth={1.6}
                                        />
                                    )}
                                    {autoDetecting
                                        ? "Analyse IA en cours…"
                                        : "Auto-détecter les devis complémentaires (IA)"}
                                </Button>
                            </div>
                        )}
                        <div className="space-y-1.5">
                            {r.devis.map((dv) => {
                                const inc = dv.status === "valide";
                                return (
                                    <label
                                        key={dv.id}
                                        className={`flex items-center justify-between gap-3 px-3 py-2 cursor-pointer border ${
                                            inc
                                                ? "border-ok bg-ok/5"
                                                : "border-border bg-white hover:border-ink/40"
                                        } transition-colors`}
                                        data-testid={`budget-devis-${dv.id}`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <input
                                                type="checkbox"
                                                checked={inc}
                                                disabled={toggling === dv.id}
                                                onChange={() =>
                                                    toggleInclude(dv.id, dv.status)
                                                }
                                                data-testid={`budget-devis-toggle-${dv.id}`}
                                                className="w-4 h-4 accent-ok shrink-0"
                                            />
                                            <div className="min-w-0">
                                                <div className="font-semibold text-sm truncate flex items-center gap-1.5">
                                                    {dv.contractor_name}
                                                    {(dv.version || 1) > 1 && (
                                                        <span className="ml-1 pill border-accent text-accent bg-accent/10 text-[10px] py-0">
                                                            V{dv.version}
                                                        </span>
                                                    )}
                                                    <ReliabilityBadge
                                                        reliability={dv.reliability}
                                                        devisId={dv.id}
                                                        compact
                                                    />
                                                </div>
                                                <div className="text-[10px] text-ink-soft uppercase tracking-wide">
                                                    {dv.items_count} postes
                                                    {inc && " · retenu pour le budget"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="num font-bold text-sm shrink-0">
                                            {fmtEUR(dv.total_ht)}
                                        </div>
                                    </label>
                                );
                            })}
                        </div>
                        {r.validated_count > 0 && (
                            <div className="mt-3 pt-2 border-t border-border flex justify-between items-center text-sm">
                                <span className="font-semibold text-ok">
                                    Total retenu pour ce poste
                                </span>
                                <span className="num font-bold text-base">
                                    {fmtEUR(r.best)}
                                </span>
                            </div>
                        )}
                    </td>
                </tr>
            )}
        </>
    );
}


function BestEditor({ r, onSave, onClear, placeholder = false }) {
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(
        r.best_override != null ? String(r.best_override) : r.best ? String(r.best) : ""
    );
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!editing) {
            setValue(
                r.best_override != null
                    ? String(r.best_override)
                    : r.best
                    ? String(r.best)
                    : ""
            );
        }
        // eslint-disable-next-line
    }, [r.best, r.best_override, editing]);

    const save = async () => {
        const n = parseFloat(String(value).replace(",", "."));
        if (isNaN(n) || n <= 0) {
            setEditing(false);
            return;
        }
        setSaving(true);
        await onSave(r, n);
        setSaving(false);
        setEditing(false);
    };

    if (editing) {
        return (
            <div className="inline-flex items-center gap-1">
                <Input
                    type="number"
                    autoFocus
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") save();
                        if (e.key === "Escape") setEditing(false);
                    }}
                    className="rounded-none h-8 num text-right w-32"
                    data-testid={`best-override-input-${r.trade}`}
                />
                <button
                    onClick={save}
                    disabled={saving}
                    className="bg-ok text-white h-8 px-2 text-[11px] uppercase tracking-wide font-semibold hover:bg-ok/90"
                    data-testid={`best-override-save-${r.trade}`}
                >
                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "OK"}
                </button>
                <button
                    onClick={() => setEditing(false)}
                    className="border border-ink h-8 px-2 text-[11px] uppercase tracking-wide hover:bg-ink hover:text-white"
                >
                    ✕
                </button>
            </div>
        );
    }
    return (
        <div className="inline-flex items-center gap-1 group/best">
            {placeholder ? (
                <span className="text-ink-soft text-xs">—</span>
            ) : (
                <span className="num text-sm font-semibold">{fmtEUR(r.best)}</span>
            )}
            <button
                onClick={() => setEditing(true)}
                className="opacity-0 group-hover/best:opacity-100 transition-opacity p-0.5 hover:text-accent"
                title="Saisir manuellement la meilleure offre"
                data-testid={`best-override-edit-${r.trade}`}
            >
                <Edit3 className="w-3 h-3" strokeWidth={1.5} />
            </button>
            {r.best_mode === "manual_override" && (
                <button
                    onClick={onClear}
                    className="opacity-60 hover:opacity-100 hover:text-danger p-0.5"
                    title="Annuler la saisie manuelle et revenir au calcul automatique"
                    data-testid={`best-override-clear-${r.trade}`}
                >
                    <Trash2 className="w-3 h-3" strokeWidth={1.5} />
                </button>
            )}
        </div>
    );
}
